/**
 * Renders a score with golf-standard indicators:
 *   Eagle (−2 or better): double circle
 *   Birdie (−1): circle
 *   Par (0): nothing
 *   Bogey (+1): square
 *   Double bogey (+2): double square
 *   Triple bogey (+3 or worse): dark square with diagonal slash
 *
 * Shows gross score as primary. If net differs from gross, shows net small below.
 */
export default function ScoreIndicator({ gross, net, par, size = 'sm' }) {
  if (gross == null) {
    if (size !== 'lg') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', fontSize: '0.9375rem', color: 'var(--text-muted)' }}>–</span>
          <span style={{ fontSize: '0.6875rem', lineHeight: 1, color: 'transparent' }}>{'\u00A0'}</span>
        </div>
      )
    }
    return <span style={{ color: 'var(--text-muted)' }}>–</span>
  }

  const netScore = net ?? gross
  const diff = par != null ? gross - par : null
  const hasNetAdjustment = net != null && net !== gross

  const isLg = size === 'lg'
  const boxSize = isLg ? '48px' : '28px'
  const fontSize = isLg ? '1.25rem' : '0.9375rem'
  const borderWidth = isLg ? '2.5px' : '2px'
  const outlineWidth = isLg ? '2.5px' : '2px'
  const outlineGap = isLg ? '2px' : '1px'
  const grossFontSize = isLg ? '0.75rem' : '0.6875rem'

  let indicatorStyle = {}
  let scoreColor = 'var(--text-primary)'
  let tripleOrWorse = false

  // Par: subtle background for large size
  if (isLg && (diff == null || diff === 0)) {
    indicatorStyle = {
      borderRadius: '0.5rem',
      backgroundColor: 'var(--bg-tertiary)',
      border: '1px solid var(--border-color)',
    }
  }

  if (diff != null && diff !== 0) {
    const eagle = '#3b82f6'  // blue
    const birdie = 'var(--accent-primary)' // green
    const bogey = '#eab308'  // yellow
    const dblBogey = 'var(--accent-danger)' // red

    if (diff <= -2) {
      // Eagle or better — double circle, blue
      indicatorStyle = {
        borderRadius: '50%',
        border: `${borderWidth} solid ${eagle}`,
        outline: `${outlineWidth} solid ${eagle}`,
        outlineOffset: outlineGap,
      }
      scoreColor = eagle
    } else if (diff === -1) {
      // Birdie — single circle, green
      indicatorStyle = {
        borderRadius: '50%',
        border: `${borderWidth} solid ${birdie}`,
      }
      scoreColor = birdie
    } else if (diff === 1) {
      // Bogey — single square, yellow
      indicatorStyle = {
        borderRadius: '2px',
        border: `${borderWidth} solid ${bogey}`,
      }
      scoreColor = bogey
    } else if (diff === 2) {
      // Double bogey — double square, red
      indicatorStyle = {
        borderRadius: '2px',
        border: `${borderWidth} solid ${dblBogey}`,
        outline: `${outlineWidth} solid ${dblBogey}`,
        outlineOffset: outlineGap,
      }
      scoreColor = dblBogey
    } else if (diff >= 3) {
      // Triple bogey or worse — dark square with diagonal slash
      const tripleBogey = '#7f1d1d' // dark burgundy
      indicatorStyle = {
        borderRadius: '2px',
        border: `${borderWidth} solid ${tripleBogey}`,
        position: 'relative',
        overflow: 'hidden',
      }
      scoreColor = '#b91c1c'
      tripleOrWorse = true
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: isLg ? 'row' : 'column', alignItems: isLg && hasNetAdjustment ? 'flex-start' : 'center', gap: isLg ? '0.5rem' : '1px' }}>
      {/* Gross score (primary) — with indicator styling */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {isLg && (
          <span style={{
            fontSize: '0.5625rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            marginBottom: '2px',
          }}>
            Gross
          </span>
        )}
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: isLg ? '36px' : boxSize,
          height: isLg ? '36px' : boxSize,
          fontSize: isLg ? '1rem' : fontSize,
          fontWeight: isLg ? '800' : '700',
          color: scoreColor,
          ...indicatorStyle,
        }}>
          {tripleOrWorse && (
            <span style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}>
              <svg viewBox="0 0 28 28" style={{ width: '100%', height: '100%' }}>
                <line x1="0" y1="28" x2="28" y2="0" stroke="#7f1d1d" strokeWidth="2" />
              </svg>
            </span>
          )}
          {gross}
        </span>
      </div>
      {/* Net score (secondary) — shown right for lg, below for sm */}
      {isLg && hasNetAdjustment && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: '0.5625rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            marginBottom: '2px',
          }}>
            Net
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            fontSize: '1rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
          }}>
            {netScore}
          </span>
        </div>
      )}
      {!isLg && (
        <span style={{
          fontSize: grossFontSize,
          color: hasNetAdjustment ? '#60a5fa' : 'transparent',
          lineHeight: 1,
        }}>
          {hasNetAdjustment ? netScore : '\u00A0'}
        </span>
      )}
    </div>
  )
}
